import { useEffect, type CSSProperties } from 'react';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { cn } from '@/lib/utils';
import { MANTA_ASPECT, MANTA_TEMPO, SNAP_EASING, SWIM_EASING, type MantaState } from './mantaMotion';

/**
 * The Mentara manta as a rigged SVG (specs/012-manta-character/DESIGN.md).
 *
 * Geometry is traced from `public/logos/mentara-icon-light.png` (744 x 644,
 * 20-unit round stroke, centerline residual about 1 unit). Every stroke keeps
 * the logo's single line weight and `currentColor`, so the parent picks the
 * brand colour (`text-primary`) and the dark theme follows for free.
 *
 * Rig (v2): named groups with their pivots set in user units. Each moving part
 * has two nested groups with the same pivot: the outer one plays the one-shot
 * gestures (arrive, master, nod, finale) as keyframes; the inner one is driven
 * by continuous oscillators, two sinusoids (`--m-beat`, the wing beat, and
 * `--m-breath`, the idle breath) that run in every state and never restart, so
 * a state change only blends amplitudes (`--m-flap`, `--m-drag`, ...) over
 * `MANTA_TEMPO.blendMs` and the wings never jump back to a rest pose. Only
 * `transform` and `opacity` ever animate; nothing here changes layout.
 * Facing, bank, look direction and speed are CSS variables, so the parent can
 * drive them per frame without a re-render. v1 of this rig is frozen in `./v1/`.
 */
export interface MantaCharacterProps {
  state?: MantaState;
  /** -1 mirrors the character to face left. The logo faces right. */
  facing?: -1 | 1;
  /** Screen-space roll in degrees, positive = nose down. Settles over `MANTA_TEMPO.bankMs`. */
  bank?: number;
  /** Where the eyes glance, each axis -1..1 (screen axes). null = straight ahead. */
  lookAt?: { x: number; y: number } | null;
  /** Width in px (height follows the logo's aspect). Default: fills the parent. */
  size?: number;
  /** Multiplies every duration. 1 = the spec tempo; 2 = twice as fast. */
  speed?: number;
  /** Force the still pose. Defaults to the user's `prefers-reduced-motion`. */
  reducedMotion?: boolean;
  /** When set, the SVG is an image with this accessible name. Otherwise it is decorative. */
  title?: string;
  /** Stroke weight in viewBox units. 20 is the logo; the component thickens it under 40px. */
  strokeWidth?: number;
  /** Freeze every animation where it is (e.g. while the stage is scrolled out of view, so the
   *  oscillators stop costing a style recalc per frame). Unlike `reducedMotion`, the pose holds. */
  paused?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function MantaCharacter({
  state = 'idle',
  facing = 1,
  bank = 0,
  lookAt = null,
  size,
  speed = 1,
  reducedMotion,
  title,
  strokeWidth,
  paused,
  className,
  style,
}: MantaCharacterProps) {
  const prefersReduced = usePrefersReducedMotion();
  const reduced = reducedMotion ?? prefersReduced;
  useMantaStyles();

  const small = size != null && size < 40;
  const sw = strokeWidth ?? (small ? 28 : 20);
  const vars = {
    '--m-facing': facing,
    '--m-bank': `${reduced ? 0 : bank}deg`,
    '--m-look-x': lookAt ? clamp1(lookAt.x) : 0,
    '--m-look-y': lookAt ? clamp1(lookAt.y) : 0,
    '--m-speed': Math.max(0.1, speed),
    ...(size != null ? { width: size, height: Math.round(size * MANTA_ASPECT) } : null),
    ...style,
  } as CSSProperties;

  return (
    <svg
      viewBox="0 0 744 644"
      className={cn('manta', className)}
      style={vars}
      data-state={reduced ? 'still' : state}
      data-reduced={reduced ? 'true' : undefined}
      data-facing={facing}
      data-paused={paused ? 'true' : undefined}
      data-rig="v2"
      data-testid="manta"
      width={size}
      height={size != null ? Math.round(size * MANTA_ASPECT) : undefined}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {title && <title>{title}</title>}
      {/* pose: facing + bank, both transitions. float: gestures; float-osc: breath and beat. */}
      <g className="m-pose">
        <g className="m-float">
          <g className="m-float-osc">
            <g className="m-tail" data-part="tail">
              <g className="m-tail-osc">
                <path d="M279,535 C240,546 200,548 160,545 C125,544 95,556 68,568 C52,574 40,586 29,601 C20,615 10,631 2,639" />
                <path d="M345,509 C345,523 331,535 317,544 C298,556 275,568 250,575 C225,579 190,574 150,573 C115,574 85,572 60,577" />
              </g>
            </g>
            <g className="m-wing-far" data-part="wing-far">
              <g className="m-wing-far-osc">
                <path d="M430,202 C380,201 310,190 240,164 C185,152 130,147 102,163 C85,167 75,181 76,189 C76,196 83,206 98,214 C129,235 157,265 176,296 C195,328 208,360 221,390 C232,411 249,430 277,449" />
                <path d="M270,456 C290,448 310,438 332,427" />
                <path d="M274,454 C274,464 262,470 262,482 C262,498 302,490 310,496 C315,502 297,516 283,526 C278,530 273,533 267,535" />
              </g>
            </g>
            <g className="m-body" data-part="body">
              <path d="M449,96 L447,156 C444,169 436,183 425,191 C418,194 414,196 410,196" />
              <path d="M448,170 C458,160 478,158 492,159 C513,161 519,179 541,184 C562,192 600,189 630,186" />
              <path d="M624,96 L624,186 C626,222 627,264 609,303 C604,307 602,311 600,315" />
              <path d="M602,311 C595,331 583,351 565,372 C539,403 496,439 451,456 C429,467 415,477 409,491 L409,504" />
              <path d="M398,512 C420,513 470,513 529,501 C542,497 557,495 571,489" />
              <path d="M392,508 C388,528 384,544 372,552" />
              <g className="m-face" data-part="face">
                <g className="m-eyes">
                  <circle className="m-eye" cx="500" cy="212" r="12" fill="currentColor" stroke="none" />
                  <circle className="m-eye" cx="570" cy="229" r="12" fill="currentColor" stroke="none" />
                </g>
                <path className="m-smile" d="M501,249 C504,274 544,283 553,257" />
              </g>
              <g className="m-cap" data-part="cap">
                <path d="M403,70 L539,10 L676,70 L539,131 Z" />
                {!small && <path d="M546,65 L655,65" />}
                <g className="m-tassel" data-part="tassel">
                  <g className="m-tassel-osc">
                    <path d="M676,70 L677,184" />
                    {!small && <circle cx="677" cy="131" r="13" fill="currentColor" stroke="none" />}
                    {!small && <circle cx="678" cy="172" r="13" fill="currentColor" stroke="none" />}
                  </g>
                </g>
              </g>
            </g>
            <g className="m-wing-near" data-part="wing-near">
              <g className="m-wing-near-osc">
                <path d="M610,311 C624,321 642,335 659,357 C697,395 727,436 735,480 C737,488 736,496 731,498 C726,501 713,500 696,496 C665,484 630,482 602,485 C576,486 554,492 528,500 C508,506 490,512 470,514" />
              </g>
            </g>
          </g>
        </g>
      </g>
    </svg>
  );
}

const clamp1 = (v: number) => Math.max(-1, Math.min(1, v));

// ── styles: injected once per document ───────────────────────────────────────

const STYLE_ID = 'manta-character-css';
let styleUsers = 0;

function useMantaStyles() {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    styleUsers += 1;
    if (!document.getElementById(STYLE_ID)) {
      const el = document.createElement('style');
      el.id = STYLE_ID;
      el.textContent = MANTA_CSS;
      document.head.appendChild(el);
    }
    return () => {
      styleUsers -= 1;
      if (styleUsers <= 0) {
        styleUsers = 0;
        document.getElementById(STYLE_ID)?.remove();
      }
    };
  }, []);
}

const T = MANTA_TEMPO;
/** Duration helper: `ms` at speed 1, divided by the speed variable. */
const d = (ms: number) => `calc(${ms}ms / var(--m-speed, 1))`;

/** A registered custom property: typed, inherited, so it can be animated and transitioned. */
const prop = (name: string, syntax: string, initial: string) =>
  `@property ${name} { syntax: '${syntax}'; inherits: true; initial-value: ${initial}; }`;

/**
 * A sinusoid in a custom property: 0 -> 1 -> -1 -> 0 with sine-shaped segments
 * (sine-out, sine-in-out, sine-in), so velocity is continuous at every joint and
 * the wave is smooth, not four eased ramps.
 */
const osc = (keyframes: string, name: string) => `@keyframes ${keyframes} {
  0% { ${name}: 0; animation-timing-function: cubic-bezier(.61,1,.88,1); }
  25% { ${name}: 1; animation-timing-function: cubic-bezier(.37,0,.63,1); }
  75% { ${name}: -1; animation-timing-function: cubic-bezier(.12,0,.39,0); }
  100% { ${name}: 0; }
}`;

const OSCILLATORS: ReadonlyArray<readonly [prop: string, keyframes: string, periodMs: number, delayMs: number]> = [
  ['--m-beat', 'mm-beat', T.swimCycleMs, 0],
  ['--m-beat-far', 'mm-beat-far', T.swimCycleMs, -70],
  ['--m-beat-tail', 'mm-beat-tail', T.swimCycleMs, -160],
  ['--m-breath', 'mm-breath', T.idleCycleMs, 0],
  ['--m-breath-b', 'mm-breath-b', T.idleCycleMs, -260],
  ['--m-breath-c', 'mm-breath-c', T.idleCycleMs, -500],
  ['--m-breath-d', 'mm-breath-d', T.idleCycleMs, -700],
];

/** Amplitudes the states set; every one of them is 0 at rest (the logo). */
const AMPLITUDES: ReadonlyArray<readonly [name: string, syntax: string, initial: string]> = [
  ['--m-flap', '<angle>', '0deg'],
  ['--m-lift', '<length>', '0px'],
  ['--m-rise', '<length>', '0px'],
  ['--m-pitch', '<angle>', '0deg'],
  ['--m-trim', '<angle>', '0deg'],
  ['--m-tail-swing', '<angle>', '0deg'],
  ['--m-flutter', '<angle>', '0deg'],
  ['--m-drag', '<angle>', '0deg'],
  ['--m-ahead', '<length>', '0px'],
  ['--m-bob', '<length>', '0px'],
  ['--m-roll', '<angle>', '0deg'],
  ['--m-wing-breath', '<angle>', '0deg'],
  ['--m-tail-breath', '<angle>', '0deg'],
  ['--m-tassel-breath', '<angle>', '0deg'],
];

/**
 * Wing hinge (v2.1, 2026-10-06). A wing is one open stroke that touches the body at two
 * places: its root and the far end of its trailing edge. A rigid rotation about one of them
 * swings the other off the body (the owner saw the near wing's trailing edge poke out below
 * the base bar on every downstroke). So each wing bends instead: an affine map that keeps the
 * line through both attachment points fixed and sends the tip exactly where a rotation by the
 * wing angle about the root would. Linear part, about the root P (the transform origin):
 *   L(a) = I + (cos a - 1) M + sin a N,  M = w a1^T,  N = w_perp a1^T
 * with w = tip - P, u = attach - P, a1 = (u_y, -u_x) / (w x u). L u = u, L w = R(a) w.
 * Computed once (specs/012-manta-character/DESIGN.md §9.5); stroke width stays within 5% at
 * swim amplitude and about 20% at the master flick's 15 degree peak.
 */
interface WingHinge {
  /** root, the transform origin */
  origin: readonly [number, number];
  M: readonly [number, number, number, number]; // m11, m12, m21, m22
  N: readonly [number, number, number, number];
}

export function wingHinge(root: readonly [number, number], attach: readonly [number, number], tip: readonly [number, number]): WingHinge {
  const w = [tip[0] - root[0], tip[1] - root[1]];
  const u = [attach[0] - root[0], attach[1] - root[1]];
  const det = w[0] * u[1] - u[0] * w[1];
  const a1 = [u[1] / det, -u[0] / det];
  const wp = [-w[1], w[0]];
  const r = (v: number) => Math.round(v * 1e5) / 1e5;
  return {
    origin: root,
    M: [r(w[0] * a1[0]), r(w[0] * a1[1]), r(w[1] * a1[0]), r(w[1] * a1[1])],
    N: [r(wp[0] * a1[0]), r(wp[0] * a1[1]), r(wp[1] * a1[0]), r(wp[1] * a1[1])],
  };
}

/** Near wing: root at the shoulder split, trailing edge ends on the base bar, tip at the rounded corner. */
export const NEAR_WING = wingHinge([612, 312], [470, 514], [735, 486]);
/** Far wing: root where the leading edge leaves the head, attach at the trailing edge's foot above the curl. */
export const FAR_WING = wingHinge([430, 202], [277, 449], [76, 189]);

/** CSS `matrix()` for a hinge at the angle expression `a` (any CSS <angle> calc). */
export function hingeMatrix(h: WingHinge, a: string): string {
  const term = (i: number, base: 0 | 1) => `calc(${base} + (cos(${a}) - 1) * ${h.M[i]} + sin(${a}) * ${h.N[i]})`;
  // matrix(a, b, c, d) = (L11, L21, L12, L22)
  return `matrix(${term(0, 1)}, ${term(2, 0)}, ${term(1, 0)}, ${term(3, 1)}, 0, 0)`;
}

/**
 * The finale loop as sampled keyframes (v2.2, 2026-10-06). One continuous loop: the heading
 * follows smootherstep over the first `FINALE_LOOP_END` of the finale, so the angular velocity is
 * a single smooth bell (zero at both ends, no plateaus), and the body travels a circle of radius
 * `FINALE_LOOP_R` (forward and up, over the top, back down to where it started), banked along
 * its own tangent. No landing dip, no scale undershoot. Sampled every 1%, played `linear`, so the
 * easing is the curve itself and not a per-segment ease (v2 eased each of four segments, which
 * read as four separate 90 degree turns).
 */
export const FINALE_LOOP_END = 0.72;
export const FINALE_LOOP_R = 31;
const smootherstep = (u: number) => u * u * u * (u * (6 * u - 15) + 10);

/** Pose of the body at finale progress `t` (0..1): translation in user units, rotation in degrees, scale. */
export function finaleLoopPose(t: number): { x: number; y: number; deg: number; scale: number } {
  const u = Math.max(0, Math.min(1, t / FINALE_LOOP_END));
  const p = smootherstep(u);
  const phi = 2 * Math.PI * p;
  return {
    x: FINALE_LOOP_R * Math.sin(phi),
    y: -FINALE_LOOP_R * (1 - Math.cos(phi)),
    deg: -360 * p,
    scale: 1 + 0.05 * Math.sin(Math.PI * p),
  };
}

function finaleFloatKeyframes(): string {
  const r2 = (v: number) => Math.round(v * 100) / 100 || 0;
  const frames: string[] = [];
  const steps = 72;
  for (let i = 0; i <= steps; i++) {
    const t = (FINALE_LOOP_END * i) / steps;
    const q = finaleLoopPose(t);
    frames.push(`${r2(t * 100)}% { transform: translate(${r2(q.x)}px, ${r2(q.y)}px) rotate(${r2(q.deg)}deg) scale(${r2(q.scale * 1000) / 1000}); }`);
  }
  frames.push('100% { transform: translate(0px, 0px) rotate(-360deg) scale(1); }');
  return `@keyframes mm-finale-float {\n  ${frames.join('\n  ')}\n}`;
}

/**
 * Pivots (user units, viewBox 744 x 644):
 *   wings: hinges, not pivots (see wingHinge): far root 430,202, near root 612,312 (the shoulder split),
 *   tail 330,540, cap 539,131 (board's bottom corner, on the head), tassel 676,70,
 *   body / float 470,340 (centre of mass), eyes: their own centres.
 */
export const MANTA_CSS = `
${OSCILLATORS.map(([name]) => prop(name, '<number>', '0')).join('\n')}
${AMPLITUDES.map(([name, syntax, initial]) => prop(name, syntax, initial)).join('\n')}
${prop('--m-wg', '<angle>', '0deg')}
${OSCILLATORS.map(([name, kf]) => osc(kf, name)).join('\n')}

.manta {
  display: block; overflow: visible; color: inherit;
  animation: ${OSCILLATORS.map(([, kf, period, delay]) => `${kf} ${d(period)} linear ${d(delay)} infinite`).join(', ')};
  transition: ${AMPLITUDES.map(([name]) => `${name} ${d(name === '--m-drag' ? 300 : T.blendMs)} ease-out`).join(', ')};
}
.manta .m-pose, .manta .m-float, .manta .m-float-osc, .manta [data-part], .manta [class$="-osc"] { transform-box: view-box; }
.manta .m-pose {
  transform-origin: 470px 340px;
  transform: scaleX(var(--m-facing, 1)) rotate(calc(var(--m-bank, 0deg) * var(--m-facing, 1)));
  transition: transform ${d(T.turnMs)} cubic-bezier(.45,0,.55,1);
}
.manta .m-float, .manta .m-float-osc { transform-origin: 470px 340px; }
.manta .m-wing-far-osc { transform-origin: ${FAR_WING.origin[0]}px ${FAR_WING.origin[1]}px; }
.manta .m-wing-near-osc { transform-origin: ${NEAR_WING.origin[0]}px ${NEAR_WING.origin[1]}px; }
.manta .m-tail, .manta .m-tail-osc { transform-origin: 330px 540px; }
.manta .m-cap { transform-origin: 539px 131px; }
.manta .m-tassel, .manta .m-tassel-osc { transform-origin: 676px 70px; }
.manta .m-eye { transform-box: fill-box; transform-origin: center; }
.manta .m-eyes {
  transform-origin: 535px 220px;
  transform: translate(calc(var(--m-look-x, 0) * 14px + var(--m-ahead, 0px)), calc(var(--m-look-y, 0) * 11px));
  transition: transform ${d(T.glanceMs)} ease-out;
}
.manta .m-face { transform-origin: 535px 220px; transition: transform ${d(T.glanceMs)} ease-out;
  transform: rotate(calc(var(--m-look-x, 0) * 3deg)); }

/* the oscillating layer: breath (slow) and beat (fast), each scaled by the state's amplitude */
.manta .m-float-osc { transform:
  translateY(calc(var(--m-breath) * var(--m-bob) + var(--m-beat) * var(--m-lift) + var(--m-rise)))
  rotate(calc(var(--m-breath) * var(--m-roll) + var(--m-beat) * var(--m-pitch) + var(--m-trim))); }
/* wings bend on a hinge (see wingHinge): the one-shot gestures add their angle through --m-wg */
.manta .m-wing-near-osc { transform: ${hingeMatrix(NEAR_WING, '(var(--m-breath) * var(--m-wing-breath) + var(--m-beat) * var(--m-flap) + var(--m-wg))')}; }
.manta .m-wing-far-osc  { transform: ${hingeMatrix(FAR_WING, '(var(--m-breath-b) * var(--m-wing-breath) + var(--m-beat-far) * var(--m-flap) + var(--m-wg))')}; }
.manta .m-tail-osc      { transform: rotate(calc(var(--m-breath-c) * var(--m-tail-breath) + var(--m-beat-tail) * var(--m-tail-swing))); }
.manta .m-tassel-osc    { transform: rotate(calc(var(--m-breath-d) * var(--m-tassel-breath) + var(--m-beat) * var(--m-flutter) + var(--m-drag))); }

/* idle: a slow breath. Bob 9 units, roll 1.1 degrees, wings and tail a beat behind. Blink every 4.6s. */
.manta[data-state="idle"], .manta[data-state="arrive"], .manta[data-state="master"], .manta[data-state="nod"] {
  --m-bob: 9px; --m-roll: 1.1deg; --m-wing-breath: 2.6deg; --m-tail-breath: 2.3deg; --m-tassel-breath: 4deg;
}
@keyframes mm-blink { 0%,91%,97%,100% { transform: scaleY(1); } 94% { transform: scaleY(.08); } }
.manta[data-state="idle"] .m-eye { animation: mm-blink ${d(T.blinkEveryMs)} linear infinite; }

/* swim: the beat takes over. Wings 8 degrees, the body pitches and lifts with it, the tail
   counter-sways, the tassel streams back and flutters, the eyes sit forward. All blended in. */
.manta[data-state="swim"] {
  --m-flap: -8deg; --m-lift: -3px; --m-rise: -3px; --m-pitch: -1deg; --m-trim: -.4deg;
  --m-tail-swing: 4deg; --m-flutter: -3deg; --m-drag: -29deg; --m-ahead: 6px; --m-wing-breath: .8deg;
}

/* arrive: amplitudes blend back to the breath while the gesture spreads the wings to brake,
   the tassel swings forward with the stop and the tail flicks once. */
@keyframes mm-arrive-wing  { 0% { --m-wg: 0deg; } 45% { --m-wg: -8deg; } 100% { --m-wg: 0deg; } }
@keyframes mm-arrive-float { 0% { transform: none; } 40% { transform: translateY(-3px) rotate(-1.6deg); } 100% { transform: none; } }
@keyframes mm-arrive-tassel{ 0% { transform: rotate(0); } 55% { transform: rotate(14deg); } 100% { transform: rotate(0); } }
@keyframes mm-arrive-tail  { 0% { transform: rotate(0); } 60% { transform: rotate(-3deg); } 100% { transform: rotate(0); } }
.manta[data-state="arrive"] .m-float    { animation: mm-arrive-float ${d(T.arriveMs)} ${SNAP_EASING} both; }
.manta[data-state="arrive"] .m-wing-near{ animation: mm-arrive-wing ${d(T.arriveMs)} ${SNAP_EASING} both; }
.manta[data-state="arrive"] .m-wing-far { animation: mm-arrive-wing ${d(T.arriveMs)} ${SNAP_EASING} both; animation-delay: ${d(30)}; }
.manta[data-state="arrive"] .m-tassel   { animation: mm-arrive-tassel ${d(T.arriveMs + 40)} ${SNAP_EASING} both; }
.manta[data-state="arrive"] .m-tail     { animation: mm-arrive-tail ${d(T.arriveMs)} ${SNAP_EASING} both; }

/* master: a nod and a crisp wing flick, the tassel bounces, the eyes close into a happy squint. */
@keyframes mm-master-float { 0% { transform: scale(1) rotate(0); } 28% { transform: translateY(-6px) scale(1.05) rotate(-2.4deg); } 60% { transform: translateY(2px) scale(.985) rotate(1.2deg); } 100% { transform: scale(1) rotate(0); } }
@keyframes mm-master-wing  { 0% { --m-wg: 0deg; } 30% { --m-wg: -15deg; } 62% { --m-wg: 5deg; } 100% { --m-wg: 0deg; } }
@keyframes mm-master-tassel{ 0% { transform: rotate(0); } 30% { transform: rotate(30deg); } 60% { transform: rotate(-14deg); } 82% { transform: rotate(6deg); } 100% { transform: rotate(0); } }
@keyframes mm-master-cap   { 0%,100% { transform: rotate(0); } 32% { transform: rotate(-4deg) translateY(-3px); } }
@keyframes mm-master-eye   { 0%,12% { transform: scaleY(1); } 24%,70% { transform: scaleY(.14); } 84%,100% { transform: scaleY(1); } }
@keyframes mm-master-tail  { 0%,100% { transform: rotate(0); } 35% { transform: rotate(5deg); } 70% { transform: rotate(-3deg); } }
.manta[data-state="master"] .m-float    { animation: mm-master-float ${d(T.masterMs)} ${SNAP_EASING} both; }
.manta[data-state="master"] .m-wing-near{ animation: mm-master-wing ${d(T.masterMs)} ${SNAP_EASING} both; }
.manta[data-state="master"] .m-wing-far { animation: mm-master-wing ${d(T.masterMs)} ${SNAP_EASING} both; animation-delay: ${d(40)}; }
.manta[data-state="master"] .m-tassel   { animation: mm-master-tassel ${d(T.masterMs)} ${SNAP_EASING} both; }
.manta[data-state="master"] .m-cap      { animation: mm-master-cap ${d(T.masterMs)} ${SNAP_EASING} both; }
.manta[data-state="master"] .m-eye      { animation: mm-master-eye ${d(T.masterMs)} ease-out both; }
.manta[data-state="master"] .m-tail     { animation: mm-master-tail ${d(T.masterMs)} ${SNAP_EASING} both; }

/* nod: one practice rep landed. A small dip of the head and a half wing flick, eyes briefly soft. */
@keyframes mm-nod-float { 0%,100% { transform: none; } 38% { transform: translateY(-4px) rotate(-1.8deg) scale(1.02); } 72% { transform: translateY(1px) rotate(.6deg); } }
@keyframes mm-nod-wing  { 0%,100% { --m-wg: 0deg; } 40% { --m-wg: -6deg; } }
@keyframes mm-nod-eye   { 0%,20%,100% { transform: scaleY(1); } 40%,60% { transform: scaleY(.3); } }
.manta[data-state="nod"] .m-float    { animation: mm-nod-float ${d(T.nodMs)} ${SNAP_EASING} both; }
.manta[data-state="nod"] .m-wing-near{ animation: mm-nod-wing ${d(T.nodMs)} ${SNAP_EASING} both; }
.manta[data-state="nod"] .m-wing-far { animation: mm-nod-wing ${d(T.nodMs)} ${SNAP_EASING} both; animation-delay: ${d(30)}; }
.manta[data-state="nod"] .m-eye      { animation: mm-nod-eye ${d(T.nodMs)} ease-out both; }

/* finale: one continuous loop (nose up, over the top, down; see finaleLoopPose) with the wing beat running through it, then it tips its cap. */
.manta[data-state="finale"] { --m-flap: -9deg; --m-lift: -3px; --m-tail-swing: 4deg; }
${finaleFloatKeyframes()}
@keyframes mm-finale-wing { 0%,72%,100% { --m-wg: 0deg; } 80% { --m-wg: -6deg; } }
@keyframes mm-finale-cap  { 0%,74% { transform: rotate(0); } 84%,90% { transform: translate(-10px,-22px) rotate(-18deg); } 100% { transform: rotate(0); } }
@keyframes mm-finale-tassel { 0% { transform: rotate(0); } 20% { transform: rotate(-30deg); } 50% { transform: rotate(25deg); } 72% { transform: rotate(-12deg); } 86% { transform: rotate(18deg); } 100% { transform: rotate(0); } }
@keyframes mm-finale-eye  { 0%,70% { transform: scaleY(1); } 78%,94% { transform: scaleY(.14); } 100% { transform: scaleY(1); } }
@keyframes mm-finale-tail { 0%,100% { transform: rotate(0); } 25% { transform: rotate(8deg); } 55% { transform: rotate(-7deg); } 80% { transform: rotate(3deg); } }
.manta[data-state="finale"] .m-float    { animation: mm-finale-float ${d(T.finaleMs)} linear both; }
.manta[data-state="finale"] .m-wing-near{ animation: mm-finale-wing ${d(T.finaleMs)} ease-in-out both; }
.manta[data-state="finale"] .m-wing-far { animation: mm-finale-wing ${d(T.finaleMs)} ease-in-out both; animation-delay: ${d(40)}; }
.manta[data-state="finale"] .m-cap      { animation: mm-finale-cap ${d(T.finaleMs)} ${SNAP_EASING} both; }
.manta[data-state="finale"] .m-tassel   { animation: mm-finale-tassel ${d(T.finaleMs)} ease-in-out both; }
.manta[data-state="finale"] .m-eye      { animation: mm-finale-eye ${d(T.finaleMs)} ease-out both; }
.manta[data-state="finale"] .m-tail     { animation: mm-finale-tail ${d(T.finaleMs)} ease-in-out both; }

/* paused (offscreen): hold the pose, stop the clock */
.manta[data-paused="true"], .manta[data-paused="true"] * { animation-play-state: paused !important; }

/* still (reduced motion, or the prerendered first paint): the logo, nothing moves, no transitions. */
.manta[data-state="still"], .manta[data-state="still"] * { animation: none !important; transition: none !important; }
@media (prefers-reduced-motion: reduce) {
  .manta, .manta * { animation: none !important; transition: none !important; }
}
`;

/** Re-exported for callers that build hop keyframes next to the rig. */
export { SWIM_EASING };
